import Logo from "components/Logo";
import SEO from "components/SEO";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import dayjs from "dayjs";
import { useParams, useLocation, Navigate } from "react-router-dom";
import { getValidUrl, getSafeHref } from "utils/helpers";
import { capture } from "utils/posthog";
import { useGetGallery } from "./api/queries";
import { clearUnlockToken } from "./api/mutations";
import GalleryFooter from "./Components/GalleryFooter";
import GalleryList from "./Components/GalleryList";
import GalleryLoader from "./Components/GalleryLoader";
import GallerySpinner from "./Components/GallerySpinner";
import GalleryToolbar from "./Components/GalleryToolbar";
import SingleViewModal from "./Components/SingleViewModal";
import GalleryGate from "./Components/GalleryGate";
import GalleryExpired, { isGalleryExpired } from "./Components/GalleryExpired";

/**
 * Default "oldest" date for date range filters.
 * Set to a date well before Movebooth's founding (2010s) to ensure
 * all historical content is included when no specific date filter is applied.
 * This effectively means "from the beginning" for practical purposes.
 */
const DEFAULT_OLDEST_DATE = '2000-01-01';

function GalleryPage() {
  const { slug } = useParams();
  const location = useLocation();
  const [page, setPage] = useState(0);
  const [singleOpen, setSingleOpen] = useState(false);
  const [filterPage, setFilterPage] = useState(0);
  const [gifs, setGifs] = useState([]);
  const [filters, setFilters] = useState({ start: null, end: null });
  const [filteredGifs, setFilteredGifs] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);
  const [channelData, setChannelData] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [scrolled, setScrolled] = useState(false);
  const [totalCount, setTotalCount] = useState(null);

  // Hide Intercom widget on gallery pages
  useEffect(() => {
    if (window.Intercom) {
      window.Intercom('update', { hide_default_launcher: true });
    }
    return () => {
      // Show Intercom widget again when leaving gallery page
      if (window.Intercom) {
        window.Intercom('update', { hide_default_launcher: false });
      }
    };
  }, []);
  // Compute default date range for "All Time" downloads
  // Uses conservative start date and today as end to capture all possible gallery images
  const computedDateRange = useMemo(() => {
    return {
      oldest: DEFAULT_OLDEST_DATE,
      newest: dayjs().format('YYYY-MM-DD')  // Today's date
    };
  }, []);

  const hasFilter = filters.start !== null;

  const { data, error, isLoading } = useGetGallery({
    slug,
    params: {
      page: hasFilter ? filterPage : page,
      start: hasFilter ? filters.start : undefined,
      end: hasFilter ? filters.end : undefined,
      big: true
    },
  });

  const channel = data?.channel || error?.channel;

  // Check if URL uses numeric channel ID (used for redirect logic below)
  const isNumericId = /^\d+$/.test(slug);

  // Product measurement: one event per gallery visit, keyed by slug only —
  // no gif/photo content, no viewer identity. `capture` buffers until the
  // deferred PostHog init lands (see utils/thirdParty) and no-ops keyless.
  //
  // A numeric /ch/:id URL redirects to the canonical /ch/:slug (see the
  // <Navigate> below), which remounts this page and would otherwise count one
  // visit twice, split across two identifiers. So a numeric route holds its
  // capture until channel data proves no redirect is coming (the channel's
  // canonical slug IS the numeric param); when a redirect fires, only the
  // post-redirect mount captures, under the canonical slug. The ref keeps the
  // effect single-shot per slug even as the channel dependency settles.
  const viewCapturedForSlug = useRef(null);
  useEffect(() => {
    if (!slug || viewCapturedForSlug.current === slug) return;
    if (isNumericId && (!channel?.slug || channel.slug !== slug)) return;
    viewCapturedForSlug.current = slug;
    capture("gallery_viewed", { channel_slug: slug });
  }, [slug, isNumericId, channel]);

  // Throttled scroll handler for toolbar show/hide
  useEffect(() => {
    const scrollableDiv = document.getElementById("scrollableDiv");
    if (!scrollableDiv) return;

    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(() => {
          const shouldBeScrolled = scrollableDiv.scrollTop > 200;
          setScrolled((prev) => (prev !== shouldBeScrolled ? shouldBeScrolled : prev));
          ticking = false;
        });
      }
    };

    scrollableDiv.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollableDiv.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!channelData && !!channel && firstLoad) {
      setChannelData(channel);
      setFirstLoad(false);
    }
  }, [channel, channelData, firstLoad]);

  // Clear stale unlock token when a protected-gallery error arrives.
  // Must be a useEffect — clearUnlockToken mutates localStorage and cannot
  // be called during render.
  useEffect(() => {
    if (error?.is_protected) {
      clearUnlockToken(slug);
    }
  }, [error?.is_protected, slug]);

  // Capture total count only on first successful load (don't update on pagination)
  useEffect(() => {
    if (totalCount === null && data?.total_count > 0) {
      setTotalCount(data.total_count);
    }
  }, [data?.total_count, totalCount]);

  // Accumulate gifs from paginated API responses using functional updates
  useEffect(() => {
    if (!data?.gifs) return;
    if (hasFilter) {
      if (filterPage === 0) {
        setFilteredGifs(data.gifs);
      } else {
        setFilteredGifs((prev) => [...prev, ...data.gifs]);
      }
    } else {
      if (page === 0) {
        setGifs(data.gifs);
        // Increment page so the next data set is automatically loaded
        // to prevent the virtualizer from having too few items on large screens
        setPage(1);
      } else {
        setGifs((prev) => [...prev, ...data.gifs]);
      }
    }
  }, [data, hasFilter, filterPage, page]);

  const loadMore = useCallback(() => {
    if (hasFilter) {
      setFilterPage((p) => p + 1);
    } else {
      setPage((p) => p + 1);
    }
  }, [hasFilter]);

  const handleOpenSingleView = useCallback((index) => {
    setSelectedIndex(index);
    setSingleOpen(true);
  }, []);

  const header = channelData?.header;
  const headerColor = channelData?.header_color;
  const subHeader = channelData?.sub_header;
  const logoImage =
    getValidUrl(channelData?.gallery_banner) ||
    getValidUrl(channelData?.big_logo_image_url);

  const hasMore = isLoading ? true : (data?.has_more ?? (data?.gifs?.length > 0));

  const handleFilterReset = () => {
    setFilteredGifs([]);
    setFilters({ start: null, end: null });
    setPage(0);
  };

  const handleFilterSubmit = (data) => {
    setFilterPage(0);
    setFilters({ start: data.startDate, end: data.endDate });
    setGifs([]);
  };

  // Scheme-guard the owner-supplied CTA link before it reaches an `href`.
  // A stored `javascript:`/`data:` URL would otherwise execute on a public
  // gallery visitor's click (stored DOM-XSS).
  const ctaHref = getSafeHref(channelData?.gallery_cta_link);

  const background =
    channelData?.gallery_color || channelData?.app_background_color;
  const backgroundColor = background
    ? background.includes("#")
      ? background
      : `#${background}`
    : "#000000";

  // Redirect numeric IDs to canonical slug URL
  // IMPORTANT: Preserve query strings and hash for analytics tracking
  // This must come after all hooks but before the main render
  if (isNumericId && channel?.slug && channel.slug !== slug) {
    return (
      <Navigate
        to={`/ch/${channel.slug}${location.search}${location.hash || ""}`}
        replace
      />
    );
  }

  if (isGalleryExpired(error)) {
    return <GalleryExpired />;
  }

  if (error?.is_protected) {
    // Use channel (available from error?.channel on first render) rather than
    // channelData, which lags one render cycle and would flash #000000.
    const gateColorSource = channel?.gallery_color || channel?.app_background_color;
    const gateBackgroundColor = gateColorSource
      ? gateColorSource.includes('#') ? gateColorSource : `#${gateColorSource}`
      : '#000000';
    return <GalleryGate channelId={slug} galleryColor={gateBackgroundColor} />;
  }

  return (
    <>
      <SingleViewModal
        open={singleOpen}
        setOpen={setSingleOpen}
        selectedIndex={selectedIndex}
        channel={channelData}
        allGifs={hasFilter ? filteredGifs : gifs}
        backgroundColor={backgroundColor}
        channelSlug={slug}
      />
      {!channelData && !firstLoad ? (
        <div className="w-full h-full flex justify-center items-center">
          <span className="text-2xl text-mb-dune">
            Cannot get channel detail
          </span>
        </div>
      ) : (
        <>
          <div
            className="w-full h-full overflow-auto"
            id="scrollableDiv"
            style={{
              background: backgroundColor,
            }}
          >
            <>
              <GalleryToolbar
                visible={scrolled}
                filters={filters}
                backgroundColor={backgroundColor}
                filterSubmit={handleFilterSubmit}
                filterReset={handleFilterReset}
                selectedIndex={selectedIndex}
                channel={channelData}
                computedDateRange={computedDateRange}
                logo={
                  <div className="max-w-[184px] max-h-[40px]">
                    {logoImage ? (
                      <img src={logoImage} alt="logo" className="max-w-[184px] max-h-[40px] w-auto h-auto object-contain" />
                    ) : (
                      <Logo withText={true} fill="white" />
                    )}
                  </div>
                }
              />
              <SEO
                title={
                  channelData?.name
                    ? /photo\s*booth/i.test(channelData.name)
                      ? `${channelData.name} Gallery`
                      : `${channelData.name} Photo Booth Gallery`
                    : "Photo Booth Gallery"
                }
                description={
                  channelData?.header ||
                  (channelData?.name
                    ? (/photo\s*booth/i.test(channelData.name)
                        ? `Browse moments from ${channelData.name}. Download photos and GIFs - powered by Movebooth.`
                        : `Browse photo booth moments from ${channelData.name}. Download photos and GIFs - powered by Movebooth.`)
                    : "Browse photo booth moments from this event gallery. Download photos and GIFs - powered by Movebooth.")
                }
                canonical={`${window.location.origin}/ch/${slug}`}
                ogType="website"
                ogImage={logoImage}
              />
              <div className="h-full">
                {!channelData && firstLoad && !error ? (
                  <GallerySpinner />
                ) : (
                  <div
                    style={{
                      paddingBottom: "60px",
                      position: "relative",
                      marginTop: "40px",
                    }}
                  >
                    <div className="flex flex-col items-center py-20 px-4 w-full">
                      <div className="flex flex-col gap-8 items-center w-full max-w-[480px]">
                        <div
                          className={`w-full transition-all ${
                            scrolled ? "opacity-0" : "opacity-100"
                          }`}
                        >
                          {logoImage ? (
                            <img
                              src={logoImage}
                              alt="logo"
                              className="w-full h-auto object-contain"
                            />
                          ) : (
                            <Logo withText={true} fill="white" />
                          )}
                        </div>
                        <div className="flex flex-col gap-4 items-start text-center w-full">
                          {header ? (
                            <h1
                              className="text-5xl font-headline font-bold leading-[1.12] w-full"
                              style={{
                                color: headerColor
                                  ? headerColor.includes("#")
                                    ? headerColor
                                    : `#${headerColor}`
                                  : "#fff",
                              }}
                            >
                              {header}
                            </h1>
                          ) : null}
                          {subHeader ? (
                            <p
                              className="text-2xl font-headline font-extralight leading-[1.44] w-full"
                              style={{
                                color: channelData?.sub_header_color
                                  ? channelData?.sub_header_color.includes("#")
                                    ? channelData?.sub_header_color
                                    : `#${channelData?.sub_header_color}`
                                  : "rgba(255, 255, 255, 0.8)",
                              }}
                            >
                              {subHeader}
                            </p>
                          ) : null}
                        </div>
                        {ctaHref ? (
                          <a
                            target="_blank"
                            className="inline-flex items-center justify-center gap-1 px-6 py-3 bg-white rounded-lg"
                            href={ctaHref}
                            rel="noreferrer"
                          >
                            <span
                              className="text-base font-headline font-normal leading-[1.28]"
                              style={{ color: backgroundColor }}
                            >
                              {channelData?.gallery_cta_text}
                            </span>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M3.33334 8H12.6667M12.6667 8L8.00001 3.33334M12.6667 8L8.00001 12.6667"
                                stroke={backgroundColor}
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </a>
                        ) : null}
                      </div>
                    </div>
                    <GalleryList
                      slug={slug}
                      gifs={hasFilter ? filteredGifs : gifs}
                      setGifs={hasFilter ? setFilteredGifs : setGifs}
                      error={error}
                      onImageClick={handleOpenSingleView}
                      loading={isLoading}
                      onLoadMore={loadMore}
                      hasMore={hasMore}
                    />
                    {isLoading && (hasFilter ? filteredGifs : gifs).length > 0 && (
                      <GalleryLoader />
                    )}
                  </div>
                )}
              </div>
            </>
          </div>
          <GalleryFooter slug={slug} totalCount={totalCount} />
        </>
      )}
    </>
  );
}

export default GalleryPage;
